import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {
  ALL_LANGUAGES, DEFAULT_LANGUAGE, getUrlLanguage, isLocalizedPath, localizedPath, ogLocale,
} from '../../../utils/localeRouting';

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  author,
  canonical,
  noindex = false,
  nofollow = false,
  structuredData,
  breadcrumbs,
  alternateLanguages,
  article,
  children
}) => {
  const { t, i18n } = useTranslation();
  
  const currentLang = i18n.language || 'fr';
  const siteName = t('seo.siteName', 'FlowDino');
  const defaultDescription = t('seo.defaultDescription', 'Solution complète pour la gestion et le partage d\'articles automatisé sur différents sites Vinted, Leboncoin. Optimisez votre workflow de publication avec notre plateforme innovante.');
  const defaultKeywords = t('seo.defaultKeywords', 'FlowDino, gestion articles, partage contenu, publication, workflow, plateforme');
  
  const baseUrl = process.env.REACT_APP_BASE_URL || 'https://www.flowdino.com';
  // `url` est le chemin sans préfixe de langue (/pricing). Une page traduite a une
  // URL par langue (/es/pricing) ; une page non traduite (blog…) n'existe qu'en
  // français : son canonical pointe toujours vers la version française.
  const pagePath = url || '/';
  const localized = isLocalizedPath(pagePath);
  const urlLang = getUrlLanguage() || DEFAULT_LANGUAGE;
  const pageLang = localized ? urlLang : DEFAULT_LANGUAGE;
  const fullUrl = `${baseUrl}${localizedPath(pagePath, pageLang)}`;
  const defaultImage = `${baseUrl}/images/og-flowdino-1200x630.jpg`;
  
  const seoTitle = title ? `${title} | ${siteName}` : siteName;
  const seoDescription = description || defaultDescription;
  const seoKeywords = keywords || defaultKeywords;
  const seoImage = image || defaultImage;
  
  const robotsContent = [
    noindex ? 'noindex' : 'index',
    nofollow ? 'nofollow' : 'follow'
  ].join(',');
  
  return (
    <Helmet htmlAttributes={{ lang: localized ? urlLang : currentLang }}>
      {/* Titre et métadonnées de base */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="author" content={author || siteName} />
      <meta name="robots" content={robotsContent} />
      <meta name="language" content={currentLang} />
      
      {/* URL canonique */}
      <link rel="canonical" href={canonical || fullUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={ogLocale(pageLang)} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      <meta name="twitter:creator" content="@FlowDino" />
      
      {/* Métadonnées supplémentaires pour le référencement */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#2563eb" />
      <meta name="msapplication-TileColor" content="#2563eb" />
        {/* Hreflang : une entrée par langue, uniquement pour les pages traduites
          (config/localizedRoutes.json), plus x-default vers le français. */}
      {localized && ALL_LANGUAGES.map((lang) => (
        <link key={lang} rel="alternate" hreflang={lang} href={`${baseUrl}${localizedPath(pagePath, lang)}`} />
      ))}
      {localized && (
        <link rel="alternate" hreflang="x-default" href={`${baseUrl}${localizedPath(pagePath, DEFAULT_LANGUAGE)}`} />
      )}
      
        {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": siteName,
          "description": seoDescription,
          "url": baseUrl,
          "potentialAction": {
            "@type": "SearchAction",
            "target": `${baseUrl}/articles?q={search_term_string}`,
            "query-input": "required name=search_term_string"
          }
        })}
      </script>
      
      {/* Schema.org supplémentaire */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Breadcrumbs Schema */}
      {breadcrumbs && breadcrumbs.length > 1 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs.map((item, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": item.name,
              "item": item.url.startsWith('http') ? item.url : `${baseUrl}${item.url}`
            }))
          })}
        </script>
      )}
      
      {/* Article Schema */}
      {article && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title || seoTitle,
            "description": article.description || seoDescription,
            "author": {
              "@type": "Person",
              "name": article.author || author || siteName
            },
            "datePublished": article.publishedAt,
            "dateModified": article.updatedAt || article.publishedAt,
            "image": article.image || seoImage,
            "url": fullUrl,
            "publisher": {
              "@type": "Organization",
              "name": siteName,
              "logo": {
                "@type": "ImageObject",
                "url": `${baseUrl}/react/images/logo.png`
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": fullUrl
            }
          })}
        </script>
      )}
      
      {/* Contenu personnalisé */}
      {children}
    </Helmet>
  );
};

export default SEO;
